import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'
import { selectAuth } from '../../../services/auth/selectors'
import { useVideoAutoSummaryLazyQuery } from '../queries.generated'
import { cacheStreamedMessage } from '../cacheStreamedMessage'
import { getVideoPlayer } from '../../OracleCommons/getVideoPlayer'
import { useVideoState } from '../../OracleCommons/useVideoState'
import { useTranslatedText } from '../../OracleCommons/StaticTextContext'

export interface UseVideoSummaryOptions {
  chatSessionId?: string
  language: string
}

export interface UseVideoSummaryResult {
  fetchAndCacheSummary: () => Promise<void>
  loading: boolean
  hasSummarized: boolean
}

/**
 * Hook to fetch a video summary based on current playback position and cache it.
 *
 * Encapsulates:
 * - Video.js player access and current time retrieval
 * - Video state from Redux (contentId, featureMediaId)
 * - Summary API call
 * - Apollo cache update
 */
export function useVideoSummary ({
  chatSessionId,
  language,
}: UseVideoSummaryOptions): UseVideoSummaryResult {
  const auth = useSelector(selectAuth)
  const apolloClient = useApolloClient()
  const [fetchSummary, { loading }] = useVideoAutoSummaryLazyQuery()
  const [hasSummarized, setHasSummarized] = useState(false)
  const { contentId, featureMediaId } = useVideoState()
  const userMessageText = useTranslatedText({ keys: ['videoSummary', 'userMessage'] })

  const fetchAndCacheSummary = async () => {
    if (!chatSessionId || !contentId || !featureMediaId) return

    const player = getVideoPlayer()
    if (!player?.currentTime) return
    const pausedAtSeconds = player.currentTime()

    const result = await fetchSummary({
      variables: {
        input: {
          contentId: String(contentId),
          featureMediaId: String(featureMediaId),
          pausedAtSeconds,
        },
      },
      context: {
        jwt: auth.get('jwt'),
      },
    })

    const summary = result.data?.videoAutoSummary?.summary
    if (summary) {
      cacheStreamedMessage({
        apolloClient,
        chatSessionId,
        messagePairId: uuidv4(),
        userMessage: userMessageText,
        oracleMessage: summary,
        language,
      })
      setHasSummarized(true)
    }
  }

  return {
    fetchAndCacheSummary,
    loading,
    hasSummarized,
  }
}
